/**
 * Calculate delivery price based on distance and restaurant pricing configuration
 * @param {number} distanceKm - Distance in kilometers
 * @param {object} pricingConfig - Restaurant's delivery pricing configuration
 * @param {boolean} isWoltDelivery - Whether Wolt handles the delivery (skip max distance check)
 * @returns {object} - { price, withinLimit, error, breakdown }
 */
export function calculateDeliveryPrice(distanceKm, config, isWoltDelivery = false) {
  if (!config || !config.steps) {
    return { price: 0, withinLimit: false, error: 'No delivery pricing configuration' };
  }

  // Validate and convert distanceKm to number
  const distance = parseFloat(distanceKm);
  if (isNaN(distance) || distance < 0) {
    return { price: 0, withinLimit: false, error: 'Invalid distance' };
  }
    
  // Check delivery limit ONLY for restaurant's own delivery, not Wolt
  // Wolt decides delivery availability through their API
  if (!isWoltDelivery && config.max_delivery_km && distance > config.max_delivery_km) {
    return { price: 0, withinLimit: false, error: `Levering er ikke tilgængelig for afstande over ${config.max_delivery_km} km` };
  }

  // Calculate price based on steps (bucket system)
  if (config.steps && config.steps.length > 0) {
    // Sort steps by max_km to ensure correct order
    const sortedSteps = [...config.steps].sort((a, b) => a.max_km - b.max_km);
    
    // Find which bucket the distance falls into
    let applicableStep = null;
    for (let step of sortedSteps) {
      if (distance <= step.max_km) {
        applicableStep = step;
        break;
      }
    }
    
    if (applicableStep) {
      // Distance falls within a step bucket
      return { 
        price: applicableStep.price, 
        withinLimit: true, 
        error: null,
        breakdown: `${distance.toFixed(1)} km - ${applicableStep.max_km} km bucket: ${applicableStep.price} kr`
      };
    } else {
      // Distance exceeds all steps, use the highest step price + per-km rate for extra distance
      const highestStep = sortedSteps[sortedSteps.length - 1];
      const basePrice = highestStep.price;
      const extraDistance = distance - highestStep.max_km;
      
      if (config.per_km_rate && extraDistance > 0) {
        const extraCost = extraDistance * config.per_km_rate;
        const totalPrice = basePrice + extraCost;
        
        return { 
          price: Math.round(totalPrice), 
          withinLimit: true, 
          error: null,
          breakdown: `${distance.toFixed(1)} km - ${highestStep.max_km} km: ${basePrice} kr + ${extraDistance.toFixed(1)} km × ${config.per_km_rate} kr/km: ${Math.round(extraCost)} kr = ${Math.round(totalPrice)} kr`
        };
      } else {
        // No per-km rate defined, just use the highest step price
        return { 
          price: basePrice, 
          withinLimit: true, 
          error: null,
          breakdown: `${distance.toFixed(1)} km - Over ${highestStep.max_km} km: ${basePrice} kr`
        };
      }
    }
  }

  // Fallback to legacy delivery_price
  if (config.delivery_price) {
    return { 
      price: config.delivery_price, 
      withinLimit: true, 
      error: null 
    };
  }

  return { price: 0, withinLimit: false, error: "Invalid pricing configuration" };
}

/**
 * Calculate distance between two coordinates using Google Maps Distance Matrix API
 * @param {object} origin - { lat, lng }
 * @param {object} destination - { lat, lng }
 * @returns {Promise<number>} - Distance in kilometers
 */
export function calculateDistance(origin, destination) {
  return new Promise((resolve, reject) => {
    if (!window.google || !window.google.maps) {
      reject(new Error('Google Maps API not loaded'));
      return;
    }

    // Validate coordinates
    if (!origin || !destination || 
        isNaN(parseFloat(origin.lat)) || isNaN(parseFloat(origin.lng)) ||
        isNaN(parseFloat(destination.lat)) || isNaN(parseFloat(destination.lng))) {
      reject(new Error('Invalid coordinates provided'));
      return;
    }

    const service = new window.google.maps.DistanceMatrixService();
    
    service.getDistanceMatrix({
      origins: [new window.google.maps.LatLng(origin.lat, origin.lng)],
      destinations: [new window.google.maps.LatLng(destination.lat, destination.lng)],
      travelMode: window.google.maps.TravelMode.DRIVING,
      unitSystem: window.google.maps.UnitSystem.METRIC,
      avoidHighways: false,
      avoidTolls: false
    }, (response, status) => {
      if (status === window.google.maps.DistanceMatrixStatus.OK) {
        const element = response.rows[0].elements[0];
        if (element.status === 'OK') {
          const distanceInMeters = element.distance.value;
          const distanceInKm = distanceInMeters / 1000;
          resolve(distanceInKm);
        } else {
          reject(new Error('Could not calculate distance'));
        }
      } else {
        reject(new Error('Distance Matrix API error: ' + status));
      }
    });
  });
}
